import type { MarkerClusterer } from "@googlemaps/markerclusterer";
import type { Map, MarkerWithCircle } from "../types";
import { findClusterGroup } from "../cluster";
import { MAP_SCROLL_ANIMATION_DURATION_MS, MAX_ZOOM_LEVEL } from "../const";
import { moveMapToPosition } from "./moveMapToPosition";
import { positionToLiteral } from "./index";

interface ScrollToMarkerOptions {
  map: Map;
  markerCluster: MarkerClusterer | null;
  points: MarkerWithCircle[];
  markerIndex: number;
  onComplete?: () => void;
  setIsProgramaticallyChangingBounds: (value: boolean) => void;
}

export const getPositionToCheck = (
  marker: google.maps.marker.AdvancedMarkerElement,
  markerCluster: MarkerClusterer | null,
): google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | null | undefined => {
  if (!markerCluster) return marker.position;

  const clusterGroup = findClusterGroup(markerCluster, marker);
  const isMultiItemCluster = clusterGroup?.markers && clusterGroup.markers.length > 1;

  if (!isMultiItemCluster) return marker.position;

  const clusterPosition = positionToLiteral(clusterGroup.position);
  return new google.maps.LatLng(clusterPosition.lat, clusterPosition.lng);
};

export const scrollToMarker = ({
  map,
  markerCluster,
  points,
  markerIndex,
  onComplete,
  setIsProgramaticallyChangingBounds,
}: ScrollToMarkerOptions): void => {
  if (!map || markerIndex === -1) return;

  const marker = points[markerIndex]?.marker as google.maps.marker.AdvancedMarkerElement;
  if (!marker) return;

  const targetPosition = getPositionToCheck(marker, markerCluster);
  if (!targetPosition) return;

  const currentBounds = map.getBounds();
  if (!currentBounds) return;

  const normalizedPosition = positionToLiteral(targetPosition);
  const targetLatLng = new google.maps.LatLng(normalizedPosition.lat, normalizedPosition.lng);

  if (currentBounds.contains(targetLatLng)) {
    onComplete?.();
    return;
  }

  setIsProgramaticallyChangingBounds(true);

  const currentZoom = map.getZoom();

  const wrappedOnComplete = () => {
    setIsProgramaticallyChangingBounds(false);
    onComplete?.();
  };

  moveMapToPosition(map, normalizedPosition, currentZoom ?? MAX_ZOOM_LEVEL, MAP_SCROLL_ANIMATION_DURATION_MS, wrappedOnComplete);
};
